// frontend/src/components/ServiceStaffAssignment.js
// Phase 1: UI for assigning staff members to service templates
import React, { useState, useEffect, useCallback } from "react";

/**
 * ServiceStaffAssignment - Manage which staff can perform a service
 *
 * Props:
 * - serviceId: string - The service template ID
 * - serviceName: string - Service name for display
 * - businessId: string - Business ID to fetch available staff
 * - onClose: () => void - Close the modal/panel
 * - onSave: (staffIds) => void - Callback after saving
 * - isMobile: boolean - Responsive mode
 */
const ServiceStaffAssignment = ({
  serviceId,
  serviceName,
  businessId,
  onClose,
  onSave,
  isMobile = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [availableStaff, setAvailableStaff] = useState([]);
  const [assignedStaffIds, setAssignedStaffIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

  // Fetch assigned staff and available staff
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("ogestyle_token");
      if (!token) {
        setError("Authentication required");
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };

      // Fetch currently assigned staff
      const assignedRes = await fetch(
        `${API_BASE_URL}/api/services/templates/${serviceId}/staff`,
        { headers },
      );

      if (!assignedRes.ok) {
        throw new Error("Failed to fetch assigned staff");
      }

      const assignedData = await assignedRes.json();
      setAssignedStaffIds(assignedData.assignedStaff?.map((s) => s._id) || []);

      // Fetch all professionals in the business
      const staffRes = await fetch(
        `${API_BASE_URL}/api/staff/business/${businessId}/professionals`,
        { headers },
      );

      if (staffRes.ok) {
        const staffData = await staffRes.json();
        setAvailableStaff(staffData.professionals || staffData || []);
      }
    } catch (err) {
      setError(err.message || "Failed to load staff data");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, serviceId, businessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Toggle staff assignment
  const handleToggleStaff = (staffId) => {
    setAssignedStaffIds((prev) => {
      if (prev.includes(staffId)) {
        return prev.filter((id) => id !== staffId);
      } else {
        return [...prev, staffId];
      }
    });
  };

  // Select all staff
  const handleSelectAll = () => {
    setAssignedStaffIds(availableStaff.map((s) => s._id));
  };

  // Clear all assignments
  const handleClearAll = () => {
    setAssignedStaffIds([]);
  };

  // Save assignments
  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const token = localStorage.getItem("ogestyle_token");
      if (!token) {
        setError("Authentication required");
        return;
      }

      const res = await fetch(
        `${API_BASE_URL}/api/services/templates/${serviceId}/staff`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ staffIds: assignedStaffIds }),
        },
      );

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed to save assignments");
      }

      setSuccess("Staff assignments saved successfully!");

      if (onSave) {
        onSave(assignedStaffIds);
      }

      setTimeout(() => {
        setSuccess("");
      }, 3000);
    } catch (err) {
      setError(err.message || "Failed to save assignments");
    } finally {
      setSaving(false);
    }
  };

  // Filter staff by search term
  const filteredStaff = availableStaff.filter((staff) => {
    if (!searchTerm) return true;
    const fullName =
      `${staff.firstName || ""} ${staff.lastName || ""}`.toLowerCase();
    const type = (staff.primaryProfessionalType || "").toLowerCase();
    return (
      fullName.includes(searchTerm.toLowerCase()) ||
      type.includes(searchTerm.toLowerCase())
    );
  });

  // Styles
  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: isMobile ? "16px" : "32px",
  };

  const modalStyle = {
    background: "#fff",
    borderRadius: "12px",
    width: "100%",
    maxWidth: "600px",
    maxHeight: "80vh",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
  };

  const headerStyle = {
    padding: "16px 20px",
    borderBottom: "1px solid #e0e0e0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  };

  const bodyStyle = {
    flex: 1,
    overflow: "auto",
    padding: "16px 20px",
  };

  const footerStyle = {
    padding: "16px 20px",
    borderTop: "1px solid #e0e0e0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
  };

  const searchInputStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    fontSize: "14px",
    marginBottom: "12px",
  };

  const staffItemStyle = (isSelected) => ({
    display: "flex",
    alignItems: "center",
    padding: "12px",
    borderRadius: "8px",
    border: `2px solid ${isSelected ? "#1976d2" : "#e0e0e0"}`,
    background: isSelected ? "#e3f2fd" : "#fff",
    cursor: "pointer",
    transition: "all 0.2s ease",
    marginBottom: "8px",
  });

  const checkboxStyle = (isSelected) => ({
    width: "20px",
    height: "20px",
    borderRadius: "4px",
    border: `2px solid ${isSelected ? "#1976d2" : "#ccc"}`,
    background: isSelected ? "#1976d2" : "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "12px",
    flexShrink: 0,
  });

  const buttonStyle = (variant) => ({
    padding: "10px 20px",
    borderRadius: "6px",
    border: variant === "primary" ? "none" : "1px solid #ccc",
    background: variant === "primary" ? "#1976d2" : "#fff",
    color: variant === "primary" ? "#fff" : "#333",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    opacity: saving ? 0.7 : 1,
  });

  const alertStyle = (type) => ({
    padding: "12px 16px",
    borderRadius: "6px",
    marginBottom: "12px",
    background: type === "error" ? "#ffebee" : "#e8f5e9",
    color: type === "error" ? "#c62828" : "#2e7d32",
    fontSize: "14px",
  });

  if (loading) {
    return (
      <div style={overlayStyle}>
        <div style={modalStyle}>
          <div style={{ padding: "40px", textAlign: "center" }}>
            Loading staff data...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <div>
            <h3 style={{ margin: 0, fontSize: "18px" }}>Assign Staff</h3>
            <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#666" }}>
              {serviceName}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: "#666",
              padding: "4px",
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={bodyStyle}>
          {error && <div style={alertStyle("error")}>{error}</div>}
          {success && <div style={alertStyle("success")}>{success}</div>}

          <input
            type="text"
            placeholder="Search staff by name or specialty..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={searchInputStyle}
          />

          <div
            style={{
              display: "flex",
              gap: "12px",
              marginBottom: "16px",
              fontSize: "13px",
            }}
          >
            <button
              onClick={handleSelectAll}
              style={{
                ...buttonStyle("secondary"),
                padding: "6px 12px",
                fontSize: "12px",
              }}
            >
              Select All
            </button>
            <button
              onClick={handleClearAll}
              style={{
                ...buttonStyle("secondary"),
                padding: "6px 12px",
                fontSize: "12px",
              }}
            >
              Clear All
            </button>
            <span style={{ color: "#666", marginLeft: "auto" }}>
              {assignedStaffIds.length} of {availableStaff.length} selected
            </span>
          </div>

          {assignedStaffIds.length === 0 && (
            <div
              style={{
                padding: "12px",
                background: "#fff3e0",
                borderRadius: "6px",
                marginBottom: "16px",
                fontSize: "13px",
                color: "#e65100",
              }}
            >
              ⚠️ No staff selected means <strong>all professionals</strong> can
              offer this service
            </div>
          )}

          {filteredStaff.length === 0 ? (
            <div
              style={{
                padding: "40px",
                textAlign: "center",
                color: "#666",
              }}
            >
              {searchTerm
                ? "No staff match your search"
                : "No professionals available"}
            </div>
          ) : (
            filteredStaff.map((staff) => {
              const isSelected = assignedStaffIds.includes(staff._id);
              return (
                <div
                  key={staff._id}
                  style={staffItemStyle(isSelected)}
                  onClick={() => handleToggleStaff(staff._id)}
                >
                  <div style={checkboxStyle(isSelected)}>
                    {isSelected && (
                      <span style={{ color: "#fff", fontSize: "14px" }}>✓</span>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: "500", marginBottom: "2px" }}>
                      {staff.firstName || "Professional"} {staff.lastName || ""}
                    </div>
                    <div style={{ fontSize: "12px", color: "#666" }}>
                      {staff.primaryProfessionalType || staff.role === "R4"
                        ? "Freelancer"
                        : "Professional"}
                      {staff.professionalTypes?.length > 1 && (
                        <span>
                          {" "}
                          • {staff.professionalTypes.length} specialties
                        </span>
                      )}
                    </div>
                  </div>
                  {staff.avatar && (
                    <img
                      src={staff.avatar}
                      alt=""
                      style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <button onClick={onClose} style={buttonStyle("secondary")}>
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={buttonStyle("primary")}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Assignments"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServiceStaffAssignment;
